import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tag,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { FaEye } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Category,
  ContractDataFieldSectionDto,
  ContractPriority,
  ContractReportDto,
  ContractReportQueryDto,
  ContractReportSortColumn,
  ContractRisk,
  ContractStateDto,
  ContractStatusDto,
  CustomContractDataFieldDto,
  SortDirection,
  useListContractReportsQuery,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useColumnMemory } from "../../../common/entity-table/useColumnMemory";
import { EntityTable } from "../../../common/entity-table/EntityTable";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { displaySum } from "../../../common/formatting/displaySum";
import { SelectorValue } from "../../../common/input/Selector/SelectorValue";
import { TipTap } from "../../../common/input/TipTap/TipTap";
import { Layout } from "../../../common/layout/Layout";
import { useSub } from "../../../common/subscription/useSub";
import { Explanation } from "../../../common/support/Explanation/Explanation";
import { useTranslationTools } from "../../../common/useTranslationTools";
import { base64Decode, base64Encode } from "../../../common/utils/base64";
import { CONTENT_WIDTH } from "../../../config";
import { urls } from "../../../urls";
import { canViewProjects } from "../../projects/projectUtils";
import { ProfileImageRounded } from "../../sourcing-events/buyer/view-all/ProfileImageRounded";
import { ContractStatusTag } from "../edit/ContractStatusTag";
import { ContractStatusShortcuts } from "./ContractStatusShortcuts";
import { CustomContractFieldValue } from "./CustomContractFieldValue";
import { useContractReportOptions } from "./useContractReportOptions";


type FiltersState = {
  q: ContractReportQueryDto;
  sortBy?: ContractReportSortColumn;
  sortDirection?: SortDirection;
  sortByCustomField?: string;
};
type FilterUpdates = Omit<FiltersState, "q"> & { q?: Partial<ContractReportQueryDto> };

const EmbeddedWrapper = ({ children }: { children: ReactNode; showFooter?: boolean; enableScrolling?: boolean }) => (
  <>{children}</>
);

export const Contracts = ({
  embedded = false,
  defaultStatuses,
}: {
  embedded?: boolean;
  defaultStatuses?: ContractStatusDto[];
} = {}) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const authState = useLoggedInWithOrgContextState();
  const columnMemory = useColumnMemory("contracts");
  const navigate = useNavigate();
  const location = useLocation();
  const sub = useSub();
  const { getPriorityTranslation, getRiskTranslation } = useTranslationTools();

  const [richTextPreviewContent, setRichTextPreviewContent] = useState<string>();

  const getStateTranslation = (state: string) => {
    return state === ContractStateDto.Published ? t("Published") : t("Draft");
  };

  const getCustomFieldValue = (
    contract: ContractReportDto,
    fieldId: string
  ): CustomContractDataFieldDto | undefined | null => {
    if (!contract.customFields) return null;

    for (const section of Object.values(contract.customFields)) {
      const field = (section as ContractDataFieldSectionDto).fields.find((f) => f.id === fieldId);
      if (field) return field;
    }
    return null;
  };

  const [filters, setFilters] = useState<FiltersState>(() => {
    const params = new URLSearchParams(location.search);
    const sortByParam = params.get("sortBy") as ContractReportSortColumn | null;
    const sortDirectionParam = params.get("sortDirection") as SortDirection | null;
    const sortByCustomFieldParam = params.get("sortByCustomField") ?? undefined;
    const q = params.get("q");
    return {
      q: {
        owningOrganizationId: authState.selectedOrg.id,
        statuses: embedded ? defaultStatuses : [ContractStatusDto.Active, ContractStatusDto.Awarded],
        limit: 10,
        skip: 0,
        ...(q ? (JSON.parse(base64Decode(q)) as ContractReportQueryDto) : {}),
      },
      sortBy: sortByParam || undefined,
      sortDirection: sortDirectionParam || undefined,
      sortByCustomField: sortByCustomFieldParam,
    };
  });

  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const defaultColumns = useMemo(
    () =>
      new Set([
        "title",
        "counterparty",
        "type",
        "projectDetails",
        "internalParties",
        "state",
        "status",
        "startDate",
        "endDate",
        "responsible",
        "referenceNumber",
        "owner",
      ]),
    []
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get("columns")) {
      const columns = columnMemory.recall() ?? Array.from(defaultColumns);
      params.set("columns", base64Encode(JSON.stringify(columns)));
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [columnMemory, defaultColumns, location.pathname, location.search, navigate]);

  const visibleColumns = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const encodedColumns = params.get("columns");
    if (!encodedColumns) return new Set<string>(columnMemory.recall() ?? defaultColumns);
    const decoded = base64Decode(encodedColumns);
    return new Set(JSON.parse(decoded) as string[]);
  }, [location.search, defaultColumns, columnMemory]);

  const visibleCustomFields = useMemo(() => {
    const sections = authState.selectedOrg.customContractSections || {};
    const map: { [key: string]: ContractDataFieldSectionDto } = {};
    for (const section of Object.values(sections)) {
      const visibleFields = section.fields.filter((field) => visibleColumns.has(field.id));
      if (visibleFields.length > 0) {
        map[section.id] = { id: section.id, name: section.name, fields: visibleFields };
      }
    }
    return map;
  }, [authState.selectedOrg.customContractSections, visibleColumns]);

  const encodedQ = useMemo(
    () => base64Encode(JSON.stringify({ ...debouncedFilters.q, visibleCustomFields })),
    [debouncedFilters.q, visibleCustomFields]
  );

  const computeHidden = (key: string): boolean => {
    return !visibleColumns.has(key);
  };

  const getVisibleColumnsFromUrl = useCallback((): string[] => {
    const params = new URLSearchParams(location.search);
    const encoded = params.get("columns");
    if (!encoded) return columnMemory.recall() ?? Array.from(defaultColumns);
    const decoded = base64Decode(encoded);
    return JSON.parse(decoded) as string[];
  }, [columnMemory, defaultColumns, location.search]);

  const setVisibleColumnsToUrl = useCallback(
    (list: string[]): void => {
      columnMemory.remember(list);
      const params = new URLSearchParams(location.search);
      const encoded = base64Encode(JSON.stringify(list));
      params.set("columns", encoded);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    },
    [columnMemory, location.pathname, location.search, navigate]
  );

  const setDefaultColumnsToUrl = useCallback((): void => {
    columnMemory.forget();
    const params = new URLSearchParams(location.search);
    params.set("columns", base64Encode(JSON.stringify(Array.from(defaultColumns))));
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [columnMemory, defaultColumns, location.pathname, location.search, navigate]);

  const handleShowColumn = useCallback(
    (columnKey: string) => {
      const current = getVisibleColumnsFromUrl();
      const next = current.includes(columnKey) ? current : [...current, columnKey];
      setVisibleColumnsToUrl(next);
    },
    [getVisibleColumnsFromUrl, setVisibleColumnsToUrl]
  );

  const handleHideColumn = useCallback(
    (columnKey: string) => {
      const current = getVisibleColumnsFromUrl();
      const next = current.filter((k) => k !== columnKey);
      setVisibleColumnsToUrl(next);
    },
    [getVisibleColumnsFromUrl, setVisibleColumnsToUrl]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    Object.entries(debouncedFilters).forEach(([key, value]) => {
      if (key === "q") params.set(key, encodedQ);
      else if (value || (typeof value === "number" && value === 0)) params.set(key, `${value}`);
      else params.delete(key);
    });

    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [debouncedFilters, navigate, location.pathname, encodedQ, location.search]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedFilterChange = useCallback(
    debounce((filter: typeof filters) => {
      setDebouncedFilters(filter);
    }, 300),
    []
  );

  const handleFilterChange = useCallback(
    (updates: FilterUpdates, resetSkip = true) => {
      setFilters((prev) => {
        const newFilter = {
          ...prev,
          ...updates,
          q: {
            ...prev.q,
            ...(updates.q || {}),
            ...(resetSkip && { skip: 0 }),
          },
        };
        handleDebouncedFilterChange(newFilter);
        return newFilter;
      });
    },
    [handleDebouncedFilterChange]
  );

  const handleSearchChange = useCallback(
    (queryKey: keyof ContractReportQueryDto) => (value: string) => {
      handleFilterChange({ q: { [queryKey]: value } });
    },
    [handleFilterChange]
  );

  const handleQueryChange = useCallback(
    (queryKey: keyof ContractReportQueryDto, value?: number | string | SelectorValue[]) => {
      if (Array.isArray(value)) {
        handleFilterChange({ q: { [queryKey]: value.map((v) => v.value) } });
      } else {
        handleFilterChange({ q: { [queryKey]: value === "" ? undefined : value } });
      }
    },
    [handleFilterChange]
  );

  const isCustomFieldSearchable = (fieldType: CustomContractDataFieldDto["type"]): boolean => {
    return (
      fieldType === "CustomText" ||
      fieldType === "CustomEmail" ||
      fieldType === "CustomPhone" ||
      fieldType === "CustomUrl" ||
      fieldType === "CustomTag" ||
      fieldType === "CustomEnum" ||
      fieldType === "CustomRichText" ||
      fieldType === "CustomMultiEnum"
    );
  };

  const getCustomFieldSearchTerm = (fieldId: string): string | undefined => {
    const current = filters.q.customFieldSearch || [];
    const match = current.find((c) => c.fieldId === fieldId);
    return match?.searchTerm || undefined;
  };

  const getSelectedCustomBooleanValue = (fieldId: string): SelectorValue | undefined => {
    const booleanSelectionForField = filters.q.customFieldSelect?.find((config) => config.fieldId === fieldId);
    return booleanSelectionForField
      ? {
          value: String(booleanSelectionForField.selected),
          label: booleanSelectionForField.selected ? t("Yes") : t("No"),
        }
      : undefined;
  };

  const handleSelectCustomBoolean =
    ({ fieldId }: { fieldId: string }) =>
    (value: SelectorValue) => {
      const selectedString = typeof value?.value === "string" ? value.value : "";
      const existingCustomFieldSelectFilters = filters.q.customFieldSelect || [];
      const filtersWithoutThisField = existingCustomFieldSelectFilters.filter((config) => config.fieldId !== fieldId);
      const updatedCustomFieldSelectFilters =
        selectedString === ""
          ? filtersWithoutThisField.length > 0
            ? filtersWithoutThisField
            : null
          : [...filtersWithoutThisField, { fieldId, selected: selectedString === "true" }];
      handleFilterChange({ q: { ...filters.q, customFieldSelect: updatedCustomFieldSelectFilters } });
    };

  const handleCustomFieldSearchChange = useCallback(
    (fieldId: string, searchTerm: string) => {
      const prev = filters.q.customFieldSearch || [];
      const without = prev.filter((c) => c.fieldId !== fieldId);
      const next = searchTerm && searchTerm.trim() !== "" ? [...without, { fieldId, searchTerm }] : without;
      handleFilterChange({ q: { ...filters.q, customFieldSearch: next.length > 0 ? next : null } });
    },
    [filters.q, handleFilterChange]
  );

  const createCustomFieldOnSearch = useCallback(
    (fieldId: string) => (value: string) => handleCustomFieldSearchChange(fieldId, value),
    [handleCustomFieldSearchChange]
  );

  const { data, isLoading, isFetching } = useListContractReportsQuery(
    {
      q: encodedQ,
      sortBy: filters.sortBy,
      sortDirection: filters.sortDirection,
      sortByCustomField: filters.sortByCustomField,
    },
    { refetchOnFocus: false, refetchOnMountOrArgChange: false }
  );

  const customFields = Object.values(authState.selectedOrg.customContractSections ?? {}).flatMap(
    (section) => section.fields
  );

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const [optimisticSelectedValues, setOptimisticSelectedValues] = useState<Record<string, SelectorValue[]>>({});

  const [optionsSearchTerms, setOptionsSearchTerms] = useState<Partial<ContractReportQueryDto>>({});
  const handleOptionsSearchChange = useCallback((key: keyof ContractReportQueryDto, value: string) => {
    setOptionsSearchTerms((prev) => ({ ...prev, [key]: value || undefined }));
  }, []);
  const makeOptionSearchHandler = useCallback(
    (key: keyof ContractReportQueryDto) => (value: string) => handleOptionsSearchChange(key, value),
    [handleOptionsSearchChange]
  );

  const {
    projectOptions,
    projectOptionsLoading,
    projectOptionsFetching,
    selectedProjects,
    selectedProjectsLoading,
    selectedProjectsFetching,
    counterpartyOptions,
    counterpartyOptionsLoading,
    counterpartyOptionsFetching,
    selectedCounterpartyOptions,
    selectedCounterpartyOptionsLoading,
    selectedCounterpartyOptionsFetching,
    internalOwnerOptions,
    internalOwnerOptionsLoading,
    internalOwnerOptionsFetching,
    selectedInternalOwnerOptions,
    selectedInternalOwnerOptionsLoading,
    selectedInternalOwnerOptionsFetching,
    owningOrgOptions,
    owningOrgOptionsLoading,
    owningOrgOptionsFetching,
    selectedOwningOrgOptions,
    selectedOwningOrgOptionsLoading,
    selectedOwningOrgOptionsFetching,
    templateOptions,
    templateOptionsLoading,
    templateOptionsFetching,
    selectedTemplateOptions,
    selectedTemplateOptionsLoading,
    selectedTemplateOptionsFetching,
    referenceNumberOptions,
    referenceNumberOptionsLoading,
    referenceNumberOptionsFetching,
    selectedReferenceNumberOptions,
    selectedReferenceNumberOptionsLoading,
    selectedReferenceNumberOptionsFetching,
    internalPartyOptions,
    internalPartyOptionsLoading,
    internalPartyOptionsFetching,
    selectedInternalPartyOptions,
    selectedInternalPartyOptionsLoading,
    selectedInternalPartyOptionsFetching,
    currencyOptions,
    currencyOptionsLoading,
    currencyOptionsFetching,
    originSourcingEventOptions,
    originSourcingEventOptionsLoading,
    originSourcingEventOptionsFetching,
    selectedOriginSourcingEventOptions,
    selectedOriginSourcingEventOptionsLoading,
    selectedOriginSourcingEventOptionsFetching,
    publishedByOptions,
    publishedByOptionsLoading,
    publishedByOptionsFetching,
    selectedPublishedByOptions,
    selectedPublishedByOptionsLoading,
    selectedPublishedByOptionsFetching,
    terminatedByOptions,
    terminatedByOptionsLoading,
    terminatedByOptionsFetching,
    selectedTerminatedByOptions,
    selectedTerminatedByOptionsLoading,
    selectedTerminatedByOptionsFetching,
    counterpartyContactNameOptions,
    counterpartyContactNameOptionsLoading,
    counterpartyContactNameOptionsFetching,
    selectedCounterpartyContactNameOptions,
    selectedCounterpartyContactNameOptionsLoading,
    selectedCounterpartyContactNameOptionsFetching,
    counterpartyContactEmailOptions,
    counterpartyContactEmailOptionsLoading,
    counterpartyContactEmailOptionsFetching,
    selectedCounterpartyContactEmailOptions,
    selectedCounterpartyContactEmailOptionsLoading,
    selectedCounterpartyContactEmailOptionsFetching,
    counterpartyContactPhoneOptions,
    counterpartyContactPhoneOptionsLoading,
    counterpartyContactPhoneOptionsFetching,
    selectedCounterpartyContactPhoneOptions,
    selectedCounterpartyContactPhoneOptionsLoading,
    selectedCounterpartyContactPhoneOptionsFetching,
    linkedContractOptions,
    linkedContractOptionsLoading,
    linkedContractOptionsFetching,
    selectedLinkedContractOptions,
    selectedLinkedContractOptionsLoading,
    selectedLinkedContractOptionsFetching,
  } = useContractReportOptions({ debouncedFilters, openDropdown, optionsSearchTerms });

  const handleContractClick = (contract: ContractReportDto) => {
    if (contract.state === ContractStateDto.Published) navigate(urls.contracts.viewSingle.go(contract.contractId));
    else if (contract.state === ContractStateDto.Draft) navigate(urls.contracts.edit.go(contract.contractId).details);
    else throw new Error("Invalid contract status");
  };

  const handleSort = useCallback(
    (column: ContractReportSortColumn) => (direction: SortDirection) => {
      setFilters((prev) => {
        const newFilter = {
          ...prev,
          sortBy: direction ? column : undefined,
          sortByCustomField: undefined,
          sortDirection: direction,
          q: { ...prev.q, skip: 0 },
        };
        handleDebouncedFilterChange(newFilter);
        return newFilter;
      });
    },
    [handleDebouncedFilterChange]
  );

  const handleCustomFieldSort = useCallback(
    (fieldId: string) => (direction: SortDirection) => {
      setFilters((prev) => {
        const newFilter = {
          ...prev,
          sortBy: undefined,
          sortByCustomField: direction ? fieldId : undefined,
          sortDirection: direction,
          q: { ...prev.q, skip: 0 },
        };
        handleDebouncedFilterChange(newFilter);
        return newFilter;
      });
    },
    [handleDebouncedFilterChange]
  );

  const handleDropdownOpen = useCallback((dropdownKey: string) => {
    setOpenDropdown(dropdownKey);
  }, []);

  const handleDropdownClose = useCallback(() => setOpenDropdown(null), []);

  // Fixed select options
  const stateOptions: SelectorValue[] = Object.values(ContractStateDto).map((state) => ({
    value: state,
    label: getStateTranslation(state),
  }));

  const statusOptions: SelectorValue[] = Object.values(ContractStatusDto).map((status) => ({
    value: status,
    label: t(status),
  }));

  const priorityOptions: SelectorValue[] = Object.values(ContractPriority).map((priority) => ({
    value: priority,
    label: getPriorityTranslation(priority).props.children,
  }));

  const riskOptions: SelectorValue[] = Object.values(ContractRisk).map((risk) => ({
    value: risk,
    label: getRiskTranslation(risk).props.children,
  }));

  const supplierCategoryOptions: SelectorValue[] = Object.values(Category).map((supplierCategory) => ({
    value: supplierCategory,
    label: supplierCategory,
  }));

  // Convert string filters to SelectorValue arrays for multi-select components
  const selectedValues: { [key in keyof Partial<ContractReportQueryDto>]: SelectorValue[] } = useMemo(() => {
    const baseValues = {
      statuses: (filters.q.statuses
        ? filters.q.statuses
            .filter((status): status is ContractStatusDto => Boolean(status))
            .map((status) => ({
              value: status,
              label: statusOptions.find((option) => option.value === status)?.label || t(String(status)),
            }))
        : []) as SelectorValue[],
      priorityLevels: (filters.q.priorityLevels
        ? filters.q.priorityLevels.map((value) => ({
            value,
            label: priorityOptions.find((option) => option.value === value)?.label || value,
          }))
        : []) as SelectorValue[],
      riskLevels: (filters.q.riskLevels
        ? filters.q.riskLevels.map((value) => ({
            value,
            label: riskOptions.find((option) => option.value === value)?.label || value,
          }))
        : []) as SelectorValue[],
      supplierCategories: (filters.q.supplierCategories
        ? filters.q.supplierCategories.map((value) => ({
            value,
            label: supplierCategoryOptions.find((option) => option.value === value)?.label || value,
          }))
        : []) as SelectorValue[],
      projectIds: filters.q.projectIds?.length
        ? selectedProjects?.projects?.map((project) => ({
            value: project.id,
            label: project.name,
          })) || []
        : [],
      counterpartyIds: filters.q.counterpartyIds?.length
        ? selectedCounterpartyOptions?.options?.map((counterparty) => ({
            value: counterparty.value,
            label: counterparty.label,
          })) || []
        : [],
      internalOwnerIds: filters.q.internalOwnerIds?.length
        ? selectedInternalOwnerOptions?.options?.map((owner) => ({
            value: owner.value,
            label: owner.label,
          })) || []
        : [],
      internalPartyIds: filters.q.internalPartyIds?.length
        ? selectedInternalPartyOptions?.options?.map((party) => ({
            value: party.value,
            label: party.label,
          })) || []
        : [],
      templateIds: filters.q.templateIds?.length
        ? selectedTemplateOptions?.options?.map((opt) => ({ value: opt.value, label: opt.label })) || []
        : [],
      owningOrganizationIds: filters.q.owningOrganizationIds?.length
        ? selectedOwningOrgOptions?.options?.map((opt) => ({ value: opt.value, label: opt.label })) || []
        : [],
      referenceNumbers: filters.q.referenceNumbers?.length
        ? selectedReferenceNumberOptions?.options?.map((opt) => ({ value: opt.value, label: opt.label })) || []
        : [],
      currencies:
        filters.q.currencies?.map((value) => ({
          value,
          label: value,
        })) ?? [],
      originSourcingEventIds: filters.q.originSourcingEventIds?.length
        ? selectedOriginSourcingEventOptions?.options?.map((opt) => ({ value: opt.value, label: opt.label })) || []
        : [],
      publishedByIds: filters.q.publishedByIds?.length
        ? selectedPublishedByOptions?.options?.map((opt) => ({ value: opt.value, label: opt.label })) || []
        : [],
      terminatedByIds: filters.q.terminatedByIds?.length
        ? selectedTerminatedByOptions?.options?.map((opt) => ({ value: opt.value, label: opt.label })) || []
        : [],
      counterpartyContactFullNames: filters.q.counterpartyContactFullNames?.length
        ? selectedCounterpartyContactNameOptions?.options?.map((opt) => ({ value: opt.value, label: opt.label })) || []
        : [],
      counterpartyContactEmails: filters.q.counterpartyContactEmails?.length
        ? selectedCounterpartyContactEmailOptions?.options?.map((opt) => ({ value: opt.value, label: opt.label })) || []
        : [],
      counterpartyContactPhoneNumbers: filters.q.counterpartyContactPhoneNumbers?.length
        ? selectedCounterpartyContactPhoneOptions?.options?.map((opt) => ({ value: opt.value, label: opt.label })) || []
        : [],
      linkedContractIds: filters.q.linkedContractIds?.length
        ? selectedLinkedContractOptions?.options?.map((opt) => ({ value: opt.value, label: opt.label })) || []
        : [],
    };

    return {
      ...baseValues,
      ...optimisticSelectedValues,
    };
  }, [
    filters.q.priorityLevels,
    filters.q.riskLevels,
    filters.q.supplierCategories,
    filters.q.statuses,
    filters.q.projectIds?.length,
    filters.q.counterpartyIds?.length,
    filters.q.internalOwnerIds?.length,
    filters.q.internalPartyIds?.length,
    filters.q.templateIds?.length,
    filters.q.owningOrganizationIds?.length,
    filters.q.referenceNumbers?.length,
    filters.q.currencies,
    filters.q.originSourcingEventIds?.length,
    filters.q.publishedByIds?.length,
    filters.q.terminatedByIds?.length,
    filters.q.counterpartyContactFullNames?.length,
    filters.q.counterpartyContactEmails?.length,
    filters.q.counterpartyContactPhoneNumbers?.length,
    filters.q.linkedContractIds?.length,
    selectedProjects?.projects,
    selectedCounterpartyOptions?.options,
    selectedInternalOwnerOptions?.options,
    selectedInternalPartyOptions?.options,
    selectedTemplateOptions?.options,
    selectedOwningOrgOptions?.options,
    selectedReferenceNumberOptions?.options,
    selectedOriginSourcingEventOptions?.options,
    selectedPublishedByOptions?.options,
    selectedTerminatedByOptions?.options,
    selectedCounterpartyContactNameOptions?.options,
    selectedCounterpartyContactEmailOptions?.options,
    selectedCounterpartyContactPhoneOptions?.options,
    selectedLinkedContractOptions?.options,
    optimisticSelectedValues,
    priorityOptions,
    riskOptions,
    supplierCategoryOptions,
    statusOptions,
  ]);

  // Optimistic select handler to prevent flashing during updates
  const handleOptimisticSelect = useCallback(
    (queryKey: keyof typeof selectedValues, values: SelectorValue[]) => {
      setOptimisticSelectedValues((prev) => ({
        ...prev,
        [queryKey]: values,
      }));

      handleQueryChange(queryKey, values);
    },
    [handleQueryChange]
  );

  const handleResetFilters = useCallback(
    (filterInput?: {
      q?: Partial<ContractReportQueryDto>;
      sortBy?: ContractReportSortColumn;
      sortDirection?: SortDirection;
      sortByCustomField?: string;
    }) => {
      const newFilters = {
        ...filterInput,
        q: {
          limit: 10,
          skip: 0,
          ...filterInput?.q,
          owningOrganizationId: authState.selectedOrg.id,
        },
      };
      setFilters(newFilters);
      setDebouncedFilters(newFilters);
      setOpenDropdown(null);
      setOptimisticSelectedValues({});
      setOptionsSearchTerms({});
    },
    [authState.selectedOrg.id]
  );

  const handleReset = useCallback(() => {
    setDefaultColumnsToUrl();
    handleResetFilters();
  }, [setDefaultColumnsToUrl, handleResetFilters]);

  const handleSetShortcutFilters = useCallback(
    (q: Partial<ContractReportQueryDto>) => {
      handleResetFilters({ q });
    },
    [handleResetFilters]
  );

  const isActiveFilterValue = (value: unknown): boolean => {
    if (value === undefined || value === null) return false;
    if (typeof value === "string") return value.trim() !== "";
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "number") return true;
    if (typeof value === "boolean") return value === true;
    return true;
  };

  const hasActiveFilters = useMemo((): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { owningOrganizationId, limit, skip, ...filtersWithoutDefaults } = filters.q;
    const queryActive = Object.values(filtersWithoutDefaults ?? {}).some(isActiveFilterValue);
    const sortingActive = !!filters.sortBy || !!filters.sortByCustomField || !!filters.sortDirection;
    const paginationActive = typeof filters.q.skip === "number" && filters.q.skip > 0;
    return queryActive || sortingActive || paginationActive;
  }, [filters]);

  const hasColumnChanges = useMemo((): boolean => {
    if (visibleColumns.size !== defaultColumns.size) return true;
    for (const key of visibleColumns) {
      if (!defaultColumns.has(key)) return true;
    }
    for (const key of defaultColumns) {
      if (!visibleColumns.has(key)) return true;
    }
    return false;
  }, [visibleColumns, defaultColumns]);

  const Wrapper = embedded ? EmbeddedWrapper : Layout;
  return (
    <Wrapper>
      <Modal isOpen={!!richTextPreviewContent} onClose={() => setRichTextPreviewContent(undefined)} size="3xl">
        <ModalOverlay />
        <ModalContent maxH="80vh">
          <ModalHeader>{t("Preview")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <TipTap content={richTextPreviewContent || ""} editable={false} hasBorder={true} />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Grid
        height="full"
        gridTemplateColumns={`1fr minmax(0, ${CONTENT_WIDTH}) 1fr`}
        gridTemplateRows="1fr"
        templateAreas={`"leftContent content rightContent"`}
      >
        <GridItem area="content" px="2">
          <Heading as="h2" size="lg" borderBottom="1px solid" borderColor="smBorder" px="2" py="2.5">
            {t("Contracts")}
          </Heading>
          <Box maxWidth="full" overflow="hidden" pt="5" px="2">
            <ContractStatusShortcuts query={filters.q} onSetFilters={handleSetShortcutFilters} />
            <EntityTable
              onDropdownOpen={handleDropdownOpen}
              onDropdownClose={handleDropdownClose}
              onShowColumn={handleShowColumn}
              onHideColumn={handleHideColumn}
              debounceMs={500}
              hasColumnChanges={hasColumnChanges}
              columns={[
                {
                  key: "title",
                  label: t("Title"),
                  width: "240px",
                  hidden: computeHidden("title"),
                  onSort: handleSort(ContractReportSortColumn.Title),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.Title ? filters.sortDirection : undefined,
                  onSearch: (value: string) => handleSearchChange("titleFilter")(value),
                  currentSearchValue: filters.q.titleFilter ?? undefined,
                },
                {
                  key: "counterparty",
                  label: t("Counterparty"),
                  width: "240px",
                  hidden: computeHidden("counterparty"),
                  isFilterActive: (filters.q.counterpartyIds?.length ?? 0) > 0,
                  onSort: handleSort(ContractReportSortColumn.CounterpartyName),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.CounterpartyName ? filters.sortDirection : undefined,
                  onSearch: (value: string) => handleSearchChange("counterpartyName")(value),
                  currentSearchValue: filters.q.counterpartyName ?? undefined,
                  multiSelectConfig: {
                    options: counterpartyOptions?.options || [],
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("counterpartyIds", values),
                    selectedValues: selectedValues.counterpartyIds,
                    optionsCount: counterpartyOptions?.count,
                    optionsLimit: 10,
                    isLoading: counterpartyOptionsLoading || counterpartyOptionsFetching,
                    selectedValuesLoading: selectedCounterpartyOptionsLoading || selectedCounterpartyOptionsFetching,
                    onInputChange: makeOptionSearchHandler("counterpartyName"),
                  },
                },
                {
                  key: "type",
                  label: t("Type"),
                  width: "180px",
                  hidden: computeHidden("type"),
                  isFilterActive: (filters.q.templateIds?.length ?? 0) > 0,
                  onSort: handleSort(ContractReportSortColumn.TemplateName),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.TemplateName ? filters.sortDirection : undefined,
                  onSearch: (value: string) => handleSearchChange("templateNameSearchTerm")(value),
                  currentSearchValue: filters.q.templateNameSearchTerm ?? undefined,
                  multiSelectConfig: {
                    options: templateOptions?.options || [],
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("templateIds", values),
                    selectedValues: selectedValues.templateIds,
                    optionsCount: templateOptions?.count,
                    optionsLimit: 10,
                    isLoading: templateOptionsLoading || templateOptionsFetching,
                    selectedValuesLoading: selectedTemplateOptionsLoading || selectedTemplateOptionsFetching,
                    onInputChange: makeOptionSearchHandler("templateNameSearchTerm"),
                  },
                },
                {
                  key: "projectDetails",
                  label: t("Projects"),
                  width: "240px",
                  hidden: sub.hasProjectAccess ? computeHidden("projectDetails") : true,
                  isFilterActive: (filters.q.projectIds?.length ?? 0) > 0,
                  onSearch: (value: string) => handleSearchChange("projectSearchTerm")(value),
                  currentSearchValue: filters.q.projectSearchTerm ?? undefined,
                  multiSelectConfig: {
                    options: projectOptions?.options || [],
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("projectIds", values),
                    selectedValues: selectedValues.projectIds,
                    optionsCount: projectOptions?.count,
                    optionsLimit: 10,
                    isLoading: projectOptionsLoading || projectOptionsFetching,
                    selectedValuesLoading: selectedProjectsLoading || selectedProjectsFetching,
                    onInputChange: makeOptionSearchHandler("projectSearchTerm"),
                  },
                },
                {
                  key: "internalParties",
                  label: t("Internal parties"),
                  width: "200px",
                  hidden: computeHidden("internalParties"),
                  isFilterActive: (filters.q.internalPartyIds?.length ?? 0) > 0,
                  onSearch: (value: string) => handleSearchChange("internalPartySearchTerm")(value),
                  currentSearchValue: filters.q.internalPartySearchTerm ?? undefined,
                  multiSelectConfig: {
                    options: internalPartyOptions?.options || [],
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("internalPartyIds", values),
                    selectedValues: selectedValues.internalPartyIds,
                    optionsCount: internalPartyOptions?.count,
                    optionsLimit: 10,
                    isLoading: internalPartyOptionsLoading || internalPartyOptionsFetching,
                    selectedValuesLoading: selectedInternalPartyOptionsLoading || selectedInternalPartyOptionsFetching,
                    onInputChange: makeOptionSearchHandler("internalPartySearchTerm"),
                  },
                },
                {
                  key: "state",
                  label: t("State"),
                  width: "160px",
                  hidden: computeHidden("state"),
                  isFilterActive: !!filters.q.state,
                  onSort: handleSort(ContractReportSortColumn.State),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.State ? filters.sortDirection : undefined,
                  singleSelectConfig: {
                    options: stateOptions,
                    onSelect: (value: SelectorValue) =>
                      handleFilterChange({ q: { ...filters.q, state: value.value ? value.value : undefined } }),
                    selectedValue: filters.q.state ? { label: filters.q.state, value: filters.q.state } : undefined,
                  },
                },
                {
                  key: "status",
                  label: t("Status"),
                  width: "160px",
                  hidden: computeHidden("status"),
                  isFilterActive: (filters.q.statuses?.length ?? 0) > 0,
                  multiSelectConfig: {
                    options: statusOptions,
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("statuses", values),
                    selectedValues: selectedValues.statuses,
                  },
                },
                {
                  key: "startDate",
                  label: t("Start date"),
                  width: "160px",
                  hidden: computeHidden("startDate"),
                  isFilterActive: !!(filters.q.startDateFrom || filters.q.startDateTo),
                  onSort: handleSort(ContractReportSortColumn.StartDate),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.StartDate ? filters.sortDirection : undefined,
                  dateRangeConfig: {
                    onFromDateChange: (date?: string) => handleQueryChange("startDateFrom", date || ""),
                    onToDateChange: (date?: string) => handleQueryChange("startDateTo", date || ""),
                    fromDate: filters.q.startDateFrom,
                    toDate: filters.q.startDateTo,
                    fromDateLabel: String(t("From")),
                    toDateLabel: String(t("To")),
                  },
                },
                {
                  key: "endDate",
                  label: t("End date"),
                  width: "180px",
                  hidden: computeHidden("endDate"),
                  isFilterActive: !!(filters.q.endDateFrom || filters.q.endDateTo),
                  onSort: handleSort(ContractReportSortColumn.EndDate),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.EndDate ? filters.sortDirection : undefined,
                  dateRangeConfig: {
                    onFromDateChange: (date?: string) => handleQueryChange("endDateFrom", date),
                    onToDateChange: (date?: string) => handleQueryChange("endDateTo", date),
                    fromDate: filters.q.endDateFrom,
                    toDate: filters.q.endDateTo,
                    fromDateLabel: String(t("From")),
                    toDateLabel: String(t("To")),
                  },
                },
                {
                  key: "hasNoEndDate",
                  label: t("No end date"),
                  width: "180px",
                  hidden: computeHidden("hasNoEndDate"),
                  isFilterActive: typeof filters.q.hasNoEndDate === "boolean",
                  singleSelectConfig: {
                    options: [
                      { value: "true", label: t("Yes") },
                      { value: "false", label: t("No") },
                    ],
                    onSelect: (value) => {
                      const next = value.value === "" ? undefined : value.value === "true";
                      handleFilterChange({ q: { ...filters.q, hasNoEndDate: next as boolean | undefined } });
                    },
                    selectedValue:
                      typeof filters.q.hasNoEndDate === "boolean"
                        ? { value: String(filters.q.hasNoEndDate), label: filters.q.hasNoEndDate ? t("Yes") : t("No") }
                        : undefined,
                  },
                },
                {
                  key: "responsible",
                  label: t("Responsible"),
                  width: "200px",
                  hidden: computeHidden("responsible"),
                  isFilterActive: (filters.q.internalOwnerIds?.length ?? 0) > 0,
                  onSort: handleSort(ContractReportSortColumn.InternalOwnerName),
                  onSearch: (value: string) => handleSearchChange("internalOwnerSearchTerm")(value),
                  currentSearchValue: filters.q.internalOwnerSearchTerm ?? undefined,
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.InternalOwnerName ? filters.sortDirection : undefined,
                  multiSelectConfig: {
                    options: internalOwnerOptions?.options || [],
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("internalOwnerIds", values),
                    selectedValues: selectedValues.internalOwnerIds,
                    optionsCount: internalOwnerOptions?.count,
                    optionsLimit: 10,
                    isLoading: internalOwnerOptionsLoading || internalOwnerOptionsFetching,
                    selectedValuesLoading: selectedInternalOwnerOptionsLoading || selectedInternalOwnerOptionsFetching,
                    onInputChange: makeOptionSearchHandler("internalOwnerSearchTerm"),
                  },
                },
                {
                  key: "estimatedValue",
                  label: t("Estimated value"),
                  width: "160px",
                  hidden: computeHidden("estimatedValue"),
                  onSort: handleSort(ContractReportSortColumn.EstimatedValue),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.EstimatedValue ? filters.sortDirection : undefined,
                },
                {
                  key: "totalPrice",
                  label: t("Total price"),
                  width: "160px",
                  hidden: computeHidden("totalPrice"),
                  onSort: handleSort(ContractReportSortColumn.TotalPrice),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.TotalPrice ? filters.sortDirection : undefined,
                },
                {
                  key: "priority",
                  label: t("Priority"),
                  width: "180px",
                  hidden: computeHidden("priority"),
                  isFilterActive: (filters.q.priorityLevels?.length ?? 0) > 0,
                  onSort: handleSort(ContractReportSortColumn.Priority),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.Priority ? filters.sortDirection : undefined,
                  multiSelectConfig: {
                    options: priorityOptions,
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("priorityLevels", values),
                    selectedValues: selectedValues.priorityLevels,
                  },
                },
                {
                  key: "risk",
                  label: t("Risk"),
                  width: "160px",
                  hidden: computeHidden("risk"),
                  isFilterActive: (filters.q.riskLevels?.length ?? 0) > 0,
                  onSort: handleSort(ContractReportSortColumn.Risk),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.Risk ? filters.sortDirection : undefined,
                  multiSelectConfig: {
                    options: riskOptions,
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("riskLevels", values),
                    selectedValues: selectedValues.riskLevels,
                  },
                },
                {
                  key: "referenceNumber",
                  label: t("Reference"),
                  width: "200px",
                  hidden: computeHidden("referenceNumber"),
                  isFilterActive: (filters.q.referenceNumbers?.length ?? 0) > 0,
                  onSort: handleSort(ContractReportSortColumn.ReferenceNumber),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.ReferenceNumber ? filters.sortDirection : undefined,
                  onSearch: (value: string) => handleSearchChange("referenceNumberSearchTerm")(value),
                  currentSearchValue: filters.q.referenceNumberSearchTerm ?? undefined,
                  multiSelectConfig: {
                    options: referenceNumberOptions?.options || [],
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("referenceNumbers", values),
                    selectedValues: selectedValues.referenceNumbers,
                    optionsCount: referenceNumberOptions?.count,
                    isLoading: referenceNumberOptionsLoading || referenceNumberOptionsFetching,
                    selectedValuesLoading:
                      selectedReferenceNumberOptionsLoading || selectedReferenceNumberOptionsFetching,
                  },
                },
                {
                  key: "createdAt",
                  label: t("Created at"),
                  width: "160px",
                  hidden: computeHidden("createdAt"),
                  isFilterActive: !!(filters.q.createdAtFrom || filters.q.createdAtTo),
                  onSort: handleSort(ContractReportSortColumn.CreatedAt),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.CreatedAt ? filters.sortDirection : undefined,
                  dateRangeConfig: {
                    onFromDateChange: (date?: string) => handleQueryChange("createdAtFrom", date || ""),
                    onToDateChange: (date?: string) => handleQueryChange("createdAtTo", date || ""),
                    fromDate: filters.q.createdAtFrom,
                    toDate: filters.q.createdAtTo,
                    fromDateLabel: String(t("From")),
                    toDateLabel: String(t("To")),
                  },
                },
                {
                  key: "modifiedAt",
                  label: t("Modified at"),
                  width: "160px",
                  hidden: computeHidden("modifiedAt"),
                  isFilterActive: !!(filters.q.modifiedAtFrom || filters.q.modifiedAtTo),
                  onSort: handleSort(ContractReportSortColumn.ModifiedAt),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.ModifiedAt ? filters.sortDirection : undefined,
                  dateRangeConfig: {
                    onFromDateChange: (date?: string) => handleQueryChange("modifiedAtFrom", date || ""),
                    onToDateChange: (date?: string) => handleQueryChange("modifiedAtTo", date || ""),
                    fromDate: filters.q.modifiedAtFrom,
                    toDate: filters.q.modifiedAtTo,
                    fromDateLabel: String(t("From")),
                    toDateLabel: String(t("To")),
                  },
                },
                {
                  key: "publishedAt",
                  label: t("Published at"),
                  width: "160px",
                  hidden: computeHidden("publishedAt"),
                  isFilterActive: !!(filters.q.publishedAtFrom || filters.q.publishedAtTo),
                  onSort: handleSort(ContractReportSortColumn.PublishedAt),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.PublishedAt ? filters.sortDirection : undefined,
                  dateRangeConfig: {
                    onFromDateChange: (date?: string) => handleQueryChange("publishedAtFrom", date || ""),
                    onToDateChange: (date?: string) => handleQueryChange("publishedAtTo", date || ""),
                    fromDate: filters.q.publishedAtFrom,
                    toDate: filters.q.publishedAtTo,
                    fromDateLabel: String(t("From")),
                    toDateLabel: String(t("To")),
                  },
                },
                {
                  key: "version",
                  label: t("Version"),
                  width: "160px",
                  hidden: computeHidden("version"),
                },
                {
                  key: "description",
                  label: t("Description"),
                  width: "200px",
                  hidden: computeHidden("description"),
                  onSearch: (value: string) => handleSearchChange("descriptionSearchTerm")(value),
                  currentSearchValue: filters.q.descriptionSearchTerm ?? undefined,
                },
                {
                  key: "timezone",
                  label: t("Timezone"),
                  width: "160px",
                  hidden: computeHidden("timezone"),
                },
                {
                  key: "currency",
                  label: t("Currency"),
                  width: "160px",
                  hidden: computeHidden("currency"),
                  isFilterActive: (filters.q.currencies?.length ?? 0) > 0,
                  onSort: handleSort(ContractReportSortColumn.Currency),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.Currency ? filters.sortDirection : undefined,
                  multiSelectConfig: {
                    options: currencyOptions?.options || [],
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("currencies", values),
                    selectedValues: selectedValues.currencies,
                    optionsCount: currencyOptions?.count,
                    isLoading: currencyOptionsLoading || currencyOptionsFetching,
                  },
                },
                {
                  key: "createdBy",
                  label: t("Created by"),
                  width: "160px",
                  hidden: computeHidden("createdBy"),
                },
                {
                  key: "renewalDate",
                  label: t("Renewal date"),
                  width: "160px",
                  hidden: computeHidden("renewalDate"),
                  isFilterActive: !!(filters.q.renewalDateFrom || filters.q.renewalDateTo),
                  onSort: handleSort(ContractReportSortColumn.RenewalDate),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.RenewalDate ? filters.sortDirection : undefined,
                  dateRangeConfig: {
                    onFromDateChange: (date?: string) => handleQueryChange("renewalDateFrom", date || ""),
                    onToDateChange: (date?: string) => handleQueryChange("renewalDateTo", date || ""),
                    fromDate: filters.q.renewalDateFrom,
                    toDate: filters.q.renewalDateTo,
                    fromDateLabel: String(t("From")),
                    toDateLabel: String(t("To")),
                  },
                },
                {
                  key: "originSourcingEvent",
                  label: t("Origin sourcing event"),
                  width: "240px",
                  hidden: computeHidden("originSourcingEvent"),
                  isFilterActive: (filters.q.originSourcingEventIds?.length ?? 0) > 0,
                  onSort: handleSort(ContractReportSortColumn.OriginSourcingEventName),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.OriginSourcingEventName
                      ? filters.sortDirection
                      : undefined,
                  onSearch: (value: string) => handleSearchChange("originSourcingEventName")(value),
                  currentSearchValue: filters.q.originSourcingEventName ?? undefined,
                  multiSelectConfig: {
                    options: originSourcingEventOptions?.options || [],
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("originSourcingEventIds", values),
                    selectedValues: selectedValues.originSourcingEventIds,
                    optionsCount: originSourcingEventOptions?.count,
                    isLoading: originSourcingEventOptionsLoading || originSourcingEventOptionsFetching,
                    selectedValuesLoading:
                      selectedOriginSourcingEventOptionsLoading || selectedOriginSourcingEventOptionsFetching,
                    onInputChange: makeOptionSearchHandler("originSourcingEventName"),
                  },
                },
                {
                  key: "publishedBy",
                  label: t("Published by"),
                  width: "160px",
                  hidden: computeHidden("publishedBy"),
                  isFilterActive: (filters.q.publishedByIds?.length ?? 0) > 0,
                  onSort: handleSort(ContractReportSortColumn.PublishedByName),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.PublishedByName ? filters.sortDirection : undefined,
                  onSearch: (value: string) => handleSearchChange("publishedBySearchTerm")(value),
                  currentSearchValue: filters.q.publishedBySearchTerm ?? undefined,
                  multiSelectConfig: {
                    options: publishedByOptions?.options || [],
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("publishedByIds", values),
                    selectedValues: selectedValues.publishedByIds,
                    optionsCount: publishedByOptions?.count,
                    isLoading: publishedByOptionsLoading || publishedByOptionsFetching,
                    selectedValuesLoading: selectedPublishedByOptionsLoading || selectedPublishedByOptionsFetching,
                  },
                },
                {
                  key: "noticePeriod",
                  label: t("Notice period"),
                  width: "160px",
                  hidden: computeHidden("noticePeriod"),
                },
                {
                  key: "documentCount",
                  label: t("Documents"),
                  width: "160px",
                  hidden: computeHidden("documentCount"),
                },
                {
                  key: "signedDocumentCount",
                  label: t("Signed docs"),
                  width: "160px",
                  hidden: computeHidden("signedDocumentCount"),
                },
                {
                  key: "terminationDate",
                  label: t("Termination date"),
                  width: "240px",
                  hidden: computeHidden("terminationDate"),
                  isFilterActive: !!(filters.q.terminationDateFrom || filters.q.terminationDateTo),
                  dateRangeConfig: {
                    onFromDateChange: (date?: string) => handleQueryChange("terminationDateFrom", date || ""),
                    onToDateChange: (date?: string) => handleQueryChange("terminationDateTo", date || ""),
                    fromDate: filters.q.terminationDateFrom,
                    toDate: filters.q.terminationDateTo,
                    fromDateLabel: String(t("From")),
                    toDateLabel: String(t("To")),
                  },
                },
                {
                  key: "terminatedBy",
                  label: t("Terminated by"),
                  width: "240px",
                  hidden: computeHidden("terminatedBy"),
                  isFilterActive: (filters.q.terminatedByIds?.length ?? 0) > 0,
                  onSort: handleSort(ContractReportSortColumn.TerminatedByName),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.TerminatedByName ? filters.sortDirection : undefined,
                  onSearch: (value: string) => handleSearchChange("terminatedBySearchTerm")(value),
                  currentSearchValue: filters.q.terminatedBySearchTerm ?? undefined,
                  multiSelectConfig: {
                    options: terminatedByOptions?.options || [],
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("terminatedByIds", values),
                    selectedValues: selectedValues.terminatedByIds,
                    optionsCount: terminatedByOptions?.count,
                    isLoading: terminatedByOptionsLoading || terminatedByOptionsFetching,
                    selectedValuesLoading: selectedTerminatedByOptionsLoading || selectedTerminatedByOptionsFetching,
                  },
                },
                {
                  key: "terminationReason",
                  label: t("Termination reason"),
                  width: "180px",
                  hidden: computeHidden("terminationReason"),
                  onSort: handleSort(ContractReportSortColumn.TerminationReason),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.TerminationReason ? filters.sortDirection : undefined,
                },
                {
                  key: "counterpartyContactFullName",
                  label: t("Counterparty contact name"),
                  width: "240px",
                  hidden: computeHidden("counterpartyContactFullName"),
                  isFilterActive: (filters.q.counterpartyContactFullNames?.length ?? 0) > 0,
                  onSort: handleSort(ContractReportSortColumn.CounterpartyContactFullName),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.CounterpartyContactFullName
                      ? filters.sortDirection
                      : undefined,
                  onSearch: (value: string) => handleSearchChange("counterpartyContactFullName")(value),
                  currentSearchValue: filters.q.counterpartyContactFullName ?? undefined,
                  multiSelectConfig: {
                    options: counterpartyContactNameOptions?.options || [],
                    onSelect: (values: SelectorValue[]) =>
                      handleOptimisticSelect("counterpartyContactFullNames", values),
                    selectedValues: selectedValues.counterpartyContactFullNames,
                    optionsCount: counterpartyContactNameOptions?.count,
                    isLoading: counterpartyContactNameOptionsLoading || counterpartyContactNameOptionsFetching,
                    selectedValuesLoading:
                      selectedCounterpartyContactNameOptionsLoading || selectedCounterpartyContactNameOptionsFetching,
                  },
                },
                {
                  key: "counterpartyContactEmail",
                  label: t("Counterparty contact email"),
                  width: "240px",
                  hidden: computeHidden("counterpartyContactEmail"),
                  isFilterActive: (filters.q.counterpartyContactEmails?.length ?? 0) > 0,
                  onSort: handleSort(ContractReportSortColumn.CounterpartyContactEmail),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.CounterpartyContactEmail
                      ? filters.sortDirection
                      : undefined,
                  onSearch: (value: string) => handleSearchChange("counterpartyContactEmail")(value),
                  currentSearchValue: filters.q.counterpartyContactEmail ?? undefined,
                  multiSelectConfig: {
                    options: counterpartyContactEmailOptions?.options || [],
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("counterpartyContactEmails", values),
                    selectedValues: selectedValues.counterpartyContactEmails,
                    optionsCount: counterpartyContactEmailOptions?.count,
                    isLoading: counterpartyContactEmailOptionsLoading || counterpartyContactEmailOptionsFetching,
                    selectedValuesLoading:
                      selectedCounterpartyContactEmailOptionsLoading || selectedCounterpartyContactEmailOptionsFetching,
                  },
                },
                {
                  key: "counterpartyContactPhone",
                  label: t("Counterparty contact phone"),
                  width: "240px",
                  hidden: computeHidden("counterpartyContactPhone"),
                  isFilterActive: (filters.q.counterpartyContactPhoneNumbers?.length ?? 0) > 0,
                  onSort: handleSort(ContractReportSortColumn.CounterpartyContactPhoneNumber),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.CounterpartyContactPhoneNumber
                      ? filters.sortDirection
                      : undefined,
                  onSearch: (value: string) => handleSearchChange("counterpartyContactPhoneNumber")(value),
                  currentSearchValue: filters.q.counterpartyContactPhoneNumber ?? undefined,
                  multiSelectConfig: {
                    options: counterpartyContactPhoneOptions?.options || [],
                    onSelect: (values: SelectorValue[]) =>
                      handleOptimisticSelect("counterpartyContactPhoneNumbers", values),
                    selectedValues: selectedValues.counterpartyContactPhoneNumbers,
                    optionsCount: counterpartyContactPhoneOptions?.count,
                    isLoading: counterpartyContactPhoneOptionsLoading || counterpartyContactPhoneOptionsFetching,
                    selectedValuesLoading:
                      selectedCounterpartyContactPhoneOptionsLoading || selectedCounterpartyContactPhoneOptionsFetching,
                  },
                },
                {
                  key: "externalLink",
                  label: t("External link"),
                  width: "160px",
                  hidden: computeHidden("externalLink"),
                },
                {
                  key: "linkedContracts",
                  label: t("Linked contracts"),
                  width: "160px",
                  hidden: computeHidden("linkedContracts"),
                  isFilterActive: (filters.q.linkedContractIds?.length ?? 0) > 0,
                  onSearch: (value: string) => handleSearchChange("linkedContractTitleSearchTerm")(value),
                  currentSearchValue: filters.q.linkedContractTitleSearchTerm ?? undefined,
                  multiSelectConfig: {
                    options: linkedContractOptions?.options || [],
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("linkedContractIds", values),
                    selectedValues: selectedValues.linkedContractIds,
                    optionsCount: linkedContractOptions?.count,
                    isLoading: linkedContractOptionsLoading || linkedContractOptionsFetching,
                    selectedValuesLoading:
                      selectedLinkedContractOptionsLoading || selectedLinkedContractOptionsFetching,
                  },
                },
                {
                  key: "paymentTerms",
                  label: t("Payment terms"),
                  width: "160px",
                  hidden: computeHidden("paymentTerms"),
                },
                {
                  key: "pricePerMonth",
                  label: t("Price per month"),
                  width: "160px",
                  hidden: computeHidden("pricePerMonth"),
                },
                {
                  key: "pricePerYear",
                  label: t("Price per year"),
                  width: "160px",
                  hidden: computeHidden("pricePerYear"),
                },
                {
                  key: "purchasingPolicy",
                  label: t("Purchasing policy"),
                  width: "160px",
                  hidden: computeHidden("purchasingPolicy"),
                },
                {
                  key: "supplierInfo",
                  label: t("Supplier info"),
                  width: "160px",
                  hidden: computeHidden("supplierInfo"),
                },
                {
                  key: "totalLiability",
                  label: t("Total liability"),
                  width: "160px",
                  hidden: computeHidden("totalLiability"),
                },
                {
                  key: "warrantyExpiration",
                  label: t("Warranty expiration"),
                  width: "160px",
                  hidden: computeHidden("warrantyExpiration"),
                },
                {
                  key: "supplierCategory",
                  label: t("Supplier category"),
                  width: "260px",
                  hidden: computeHidden("supplierCategory"),
                  isFilterActive: (filters.q.supplierCategories?.length ?? 0) > 0,
                  onSort: handleSort(ContractReportSortColumn.SupplierCategory),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.SupplierCategory ? filters.sortDirection : undefined,
                  multiSelectConfig: {
                    options: supplierCategoryOptions,
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("supplierCategories", values),
                    selectedValues: selectedValues.supplierCategories,
                    optionsCount: supplierCategoryOptions.length,
                  },
                },
                {
                  key: "options",
                  label: t("Contract options"),
                  width: "160px",
                  hidden: computeHidden("options"),
                },
                {
                  key: "owner",
                  label: t("Owner"),
                  width: "180px",
                  hidden: computeHidden("owner"),
                  isFilterActive: (filters.q.owningOrganizationIds?.length ?? 0) > 0,
                  onSort: handleSort(ContractReportSortColumn.OwningOrganizationName),
                  currentSortDirection:
                    filters.sortBy === ContractReportSortColumn.OwningOrganizationName
                      ? filters.sortDirection
                      : undefined,
                  onSearch: (value: string) => handleSearchChange("owningOrganizationName")(value),
                  currentSearchValue: filters.q.owningOrganizationName ?? undefined,
                  multiSelectConfig: {
                    options: owningOrgOptions?.options || [],
                    onSelect: (values: SelectorValue[]) => handleOptimisticSelect("owningOrganizationIds", values),
                    selectedValues: selectedValues.owningOrganizationIds,
                    optionsCount: owningOrgOptions?.count,
                    optionsLimit: 10,
                    isLoading: owningOrgOptionsLoading || owningOrgOptionsFetching,
                    selectedValuesLoading: selectedOwningOrgOptionsLoading || selectedOwningOrgOptionsFetching,
                  },
                },
                ...customFields.map((field) => ({
                  key: field.id,
                  label: field.name,
                  width: "160px",
                  hidden: computeHidden(field.id),
                  ...(field.type !== "CustomRichText" &&
                    field.type !== "CustomMultiEnum" && {
                      onSort: handleCustomFieldSort(field.id),
                      currentSortDirection: filters.sortByCustomField === field.id ? filters.sortDirection : undefined,
                    }),
                  ...(isCustomFieldSearchable(field.type) && {
                    onSearch: createCustomFieldOnSearch(field.id),
                    currentSearchValue: getCustomFieldSearchTerm(field.id),
                  }),
                  ...(field.type === "CustomBoolean" && {
                    singleSelectConfig: {
                      options: [
                        { value: "true", label: t("Yes") },
                        { value: "false", label: t("No") },
                      ],
                      onSelect: handleSelectCustomBoolean({ fieldId: field.id }),
                      selectedValue: getSelectedCustomBooleanValue(field.id),
                    },
                  }),
                })),
                {
                  key: "actions",
                  label: t("Actions"),
                  width: "160px",
                },
              ]}
              rows={
                data?.reports?.map((contract) => [
                  // Title
                  <Tooltip key="title" label={contract.title}>
                    <Button
                      variant="unstyled"
                      textAlign="left"
                      width="full"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                      onClick={() => handleContractClick(contract)}
                    >
                      {contract.title}
                    </Button>
                  </Tooltip>,
                  contract.counterpartyName ? (
                    <Flex key="counterparty" align="center">
                      <Box>
                        <ProfileImageRounded
                          name={contract.counterpartyName}
                          backgroundColor="smSecondary"
                          width="5"
                          fontSize="xx-small"
                        />
                      </Box>
                      <Text fontSize="sm" pl="1.5" overflow="hidden" textOverflow="ellipsis">
                        {contract.counterpartyName}
                      </Text>
                    </Flex>
                  ) : (
                    <Text key="counterparty" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  // Type (Template Name)
                  contract.templateName ? (
                    <Text key="type" fontSize="sm" overflow="hidden" textOverflow="ellipsis">
                      {contract.templateName}
                    </Text>
                  ) : (
                    <Text key="type" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.projectDetails?.length ? (
                    <Flex key="projectDetails" flexDirection="column" maxWidth="full">
                      {contract.projectDetails.map((p, index) => (
                        <Tag
                          key={index}
                          size="sm"
                          width="full"
                          maxWidth="full"
                          mb="1"
                          cursor={canViewProjects(authState.selectedOrg.roles) ? "pointer" : undefined}
                          onClick={() =>
                            canViewProjects(authState.selectedOrg.roles) && navigate(urls.projects.view.go(p.projectId))
                          }
                        >
                          <Tooltip label={p.projectName} rounded="md" size="sm">
                            <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" width="full">
                              {p.projectName}
                            </Text>
                          </Tooltip>
                        </Tag>
                      ))}
                    </Flex>
                  ) : (
                    <Text key="projectDetails" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("None")}
                    </Text>
                  ),
                  <Text key="internalParties" fontSize="sm" overflow="hidden" textOverflow="ellipsis">
                    {contract.internalParties?.length ? (
                      contract.internalParties.map((p) => p.organizationName).join(", ")
                    ) : (
                      <Text as="i" color="smMuted">
                        {t("Not provided")}
                      </Text>
                    )}
                  </Text>,
                  contract.state ? (
                    <Tag key="state" size="sm" colorScheme="blue" variant="outline">
                      {getStateTranslation(contract.state)}
                    </Tag>
                  ) : (
                    <Tag key="state" size="sm" colorScheme="gray" variant="outline">
                      {t("Draft")}
                    </Tag>
                  ),
                  contract.status ? (
                    <Flex key="status">
                      <ContractStatusTag status={contract.status} />
                    </Flex>
                  ) : null,
                  contract.startDate ? (
                    <Text key="startDate" fontSize="sm">
                      {displayDate(contract.startDate)}
                    </Text>
                  ) : (
                    <Text key="startDate" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.endDate ? (
                    <Text key="endDate" fontSize="sm">
                      {displayDate(contract.endDate)}
                    </Text>
                  ) : (
                    <Text key="endDate" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  <Text key="hasNoEndDate" fontSize="sm">
                    {contract.hasNoEndDate ? t("Yes") : t("No")}
                  </Text>,
                  contract.internalOwnerName ? (
                    <Flex key="responsible" align="center">
                      <Box>
                        <ProfileImageRounded
                          name={contract.internalOwnerName}
                          backgroundColor="smPrimary"
                          width="20px"
                          fontSize="xx-small"
                        />
                      </Box>
                      <Text fontSize="sm" pl="1.5" overflow="hidden" textOverflow="ellipsis">
                        {contract.internalOwnerName}
                      </Text>
                    </Flex>
                  ) : (
                    <Text key="responsible" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.estimatedValue ? (
                    <Text key="estimatedValue" fontSize="sm">
                      {displaySum({ value: contract.estimatedValue, currency: contract.currency ?? "NOK" })}
                    </Text>
                  ) : (
                    <Text key="estimatedValue" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.totalPrice ? (
                    <Text key="totalPrice" fontSize="sm">
                      {displaySum({ value: contract.totalPrice, currency: contract.currency ?? "NOK" })}
                    </Text>
                  ) : (
                    <Text key="totalPrice" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.priority ? (
                    <Tag key="priority" size="sm" colorScheme="orange" variant="outline">
                      {getPriorityTranslation(contract.priority as ContractPriority)}
                    </Tag>
                  ) : (
                    <Text key="priority" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not set")}
                    </Text>
                  ),
                  contract.risk ? (
                    <Tag key="risk" size="sm" colorScheme="red" variant="outline">
                      {getRiskTranslation(contract.risk as ContractRisk)}
                    </Tag>
                  ) : (
                    <Text key="risk" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not set")}
                    </Text>
                  ),
                  contract.referenceNumber ? (
                    <Text key="referenceNumber" fontSize="sm">
                      {contract.referenceNumber}
                    </Text>
                  ) : (
                    <Text key="referenceNumber" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.createdAt ? (
                    <Text key="createdAt" fontSize="sm">
                      {displayDate(contract.createdAt)}
                    </Text>
                  ) : (
                    <Text key="createdAt" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.modifiedAt ? (
                    <Text key="modifiedAt" fontSize="sm">
                      {displayDate(contract.modifiedAt)}
                    </Text>
                  ) : (
                    <Text key="modifiedAt" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.publishedAt ? (
                    <Text key="publishedAt" fontSize="sm">
                      {displayDate(contract.publishedAt)}
                    </Text>
                  ) : (
                    <Text key="publishedAt" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not published")}
                    </Text>
                  ),
                  <Text key="version" fontSize="sm">
                    {contract.version}
                  </Text>,
                  contract.description ? (
                    <Tooltip key="description" label={contract.description}>
                      <Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                        {contract.description}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text key="description" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.timezone ? (
                    <Text key="timezone" fontSize="sm">
                      {contract.timezone}
                    </Text>
                  ) : (
                    <Text key="timezone" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.currency ? (
                    <Text key="currency" fontSize="sm">
                      {contract.currency}
                    </Text>
                  ) : (
                    <Text key="currency" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.createdByName ? (
                    <Flex key="createdBy" align="center">
                      <Box>
                        <ProfileImageRounded
                          name={contract.createdByName}
                          backgroundColor="smPrimary"
                          width="20px"
                          fontSize="xx-small"
                        />
                      </Box>
                      <Text fontSize="sm" pl="1.5" overflow="hidden" textOverflow="ellipsis">
                        {contract.createdByName}
                      </Text>
                    </Flex>
                  ) : (
                    <Text key="createdBy" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.renewalDate ? (
                    <Text key="renewalDate" fontSize="sm">
                      {displayDate(contract.renewalDate)}
                    </Text>
                  ) : (
                    <Text key="renewalDate" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.originSourcingEventName ? (
                    <Text key="originSourcingEvent" fontSize="sm" overflow="hidden" textOverflow="ellipsis">
                      {contract.originSourcingEventName}
                    </Text>
                  ) : (
                    <Text key="originSourcingEvent" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.publishedByName ? (
                    <Flex key="publishedBy" align="center">
                      <Box>
                        <ProfileImageRounded
                          name={contract.publishedByName}
                          backgroundColor="smPrimary"
                          width="20px"
                          fontSize="xx-small"
                        />
                      </Box>
                      <Text fontSize="sm" pl="1.5" overflow="hidden" textOverflow="ellipsis">
                        {contract.publishedByName}
                      </Text>
                    </Flex>
                  ) : (
                    <Text key="publishedBy" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.noticePeriod ? (
                    <Text key="noticePeriod" fontSize="sm">
                      {contract.noticePeriod} {t("days")}
                    </Text>
                  ) : (
                    <Text key="noticePeriod" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  <Text key="documentCount" fontSize="sm">
                    {contract.documentIds?.length || 0}
                  </Text>,
                  <Text key="signedDocumentCount" fontSize="sm">
                    {contract.signedDocumentIds?.length || 0}
                  </Text>,
                  contract.terminationDate ? (
                    <Text key="terminationDate" fontSize="sm">
                      {displayDate(contract.terminationDate)}
                    </Text>
                  ) : (
                    <Text key="terminationDate" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not terminated")}
                    </Text>
                  ),
                  contract.terminatedById ? (
                    <Text key="terminatedBy" fontSize="sm" overflow="hidden" textOverflow="ellipsis">
                      {contract.terminatedByName}
                    </Text>
                  ) : (
                    <Text key="terminatedBy" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not terminated")}
                    </Text>
                  ),
                  contract.terminationReason ? (
                    <Tooltip key="terminationReason" label={contract.terminationReason}>
                      <Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                        {contract.terminationReason}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text key="terminationReason" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not terminated")}
                    </Text>
                  ),
                  contract.counterpartyContactFullName ? (
                    <Flex key="counterpartyContactFullName" align="center">
                      <Box>
                        <ProfileImageRounded
                          name={contract.counterpartyContactFullName}
                          backgroundColor="smSecondary"
                          width="20px"
                          fontSize="xx-small"
                        />
                      </Box>
                      <Text fontSize="sm" pl="1.5" overflow="hidden" textOverflow="ellipsis">
                        {contract.counterpartyContactFullName}
                      </Text>
                    </Flex>
                  ) : (
                    <Text key="counterpartyContactFullName" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.counterpartyContactEmail ? (
                    <Text key="counterpartyContactEmail" fontSize="sm" overflow="hidden" textOverflow="ellipsis">
                      {contract.counterpartyContactEmail}
                    </Text>
                  ) : (
                    <Text key="counterpartyContactEmail" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.counterpartyContactPhoneNumber ? (
                    <Text key="counterpartyContactPhone" fontSize="sm" overflow="hidden" textOverflow="ellipsis">
                      {contract.counterpartyContactPhoneNumber}
                    </Text>
                  ) : (
                    <Text key="counterpartyContactPhone" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.externalLink ? (
                    <Button
                      key="externalLink"
                      as="a"
                      href={contract.externalLink}
                      target="_blank"
                      variant="link"
                      colorScheme="blue"
                      size="xs"
                      fontSize="sm"
                    >
                      {t("Link")}
                    </Button>
                  ) : (
                    <Text key="externalLink" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("None")}
                    </Text>
                  ),
                  contract.linkedContracts?.length ? (
                    <Text key="linkedContracts" fontSize="sm">
                      {contract.linkedContracts.length} {t("linked")}
                    </Text>
                  ) : (
                    <Text key="linkedContracts" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("None")}
                    </Text>
                  ),
                  contract.paymentTermsInDays ? (
                    <Text key="paymentTerms" fontSize="sm">
                      {contract.paymentTermsInDays} {t("days")}
                    </Text>
                  ) : (
                    <Text key="paymentTerms" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.pricePerMonth ? (
                    <Text key="pricePerMonth" fontSize="sm">
                      {displaySum({ value: contract.pricePerMonth, currency: contract.currency ?? "NOK" })}
                    </Text>
                  ) : (
                    <Text key="pricePerMonth" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.pricePerYear ? (
                    <Text key="pricePerYear" fontSize="sm">
                      {displaySum({ value: contract.pricePerYear, currency: contract.currency ?? "NOK" })}
                    </Text>
                  ) : (
                    <Text key="pricePerYear" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.purchasingPolicy ? (
                    <Tooltip key="purchasingPolicy" label={contract.purchasingPolicy}>
                      <Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                        {contract.purchasingPolicy}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text key="purchasingPolicy" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.supplierInfo ? (
                    <VStack key="supplierInfo" spacing="1" align="stretch">
                      <HStack spacing="1">
                        <Icon
                          as={contract.supplierInfo.isClimateCommitted ? CheckIcon : CloseIcon}
                          color={contract.supplierInfo.isClimateCommitted ? "green.500" : "red.500"}
                          boxSize="3"
                        />
                        <Text fontSize="xs">Climate</Text>
                      </HStack>
                      <HStack spacing="1">
                        <Icon
                          as={contract.supplierInfo.hasSignedCodeOfConduct ? CheckIcon : CloseIcon}
                          color={contract.supplierInfo.hasSignedCodeOfConduct ? "green.500" : "red.500"}
                          boxSize="3"
                        />
                        <Text fontSize="xs">Code</Text>
                      </HStack>
                      <HStack spacing="1">
                        <Icon
                          as={contract.supplierInfo.hasCustomerContact ? CheckIcon : CloseIcon}
                          color={contract.supplierInfo.hasCustomerContact ? "green.500" : "red.500"}
                          boxSize="3"
                        />
                        <Text fontSize="xs">Contact</Text>
                      </HStack>
                    </VStack>
                  ) : (
                    <Text key="supplierInfo" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.totalLiability ? (
                    <Text key="totalLiability" fontSize="sm">
                      {displaySum({ value: contract.totalLiability, currency: contract.currency ?? "NOK" })}
                    </Text>
                  ) : (
                    <Text key="totalLiability" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.warrantyExpirationDate ? (
                    <Text key="warrantyExpiration" fontSize="sm">
                      {displayDate(contract.warrantyExpirationDate)}
                    </Text>
                  ) : (
                    <Text key="warrantyExpiration" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.supplierCategory ? (
                    <Tag key="supplierCategory" size="sm" colorScheme="purple" variant="outline">
                      {contract.supplierCategory}
                    </Tag>
                  ) : (
                    <Text key="supplierCategory" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.options?.length ? (
                    <Text key="options" fontSize="sm">
                      {contract.options.length} {t("options")}
                    </Text>
                  ) : (
                    <Text key="options" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("None")}
                    </Text>
                  ),
                  contract.owningOrganizationId !== authState.selectedOrg.id ? (
                    <Explanation
                      key="owner"
                      text={t(`${contract.owningOrganizationName} shared this contract with you`)}
                      enabled={true}
                    >
                      <Text fontSize="sm" color="smSecondary">
                        {contract.owningOrganizationName}
                      </Text>
                    </Explanation>
                  ) : (
                    <Explanation key="owner" text={t("Your organization owns this contract")} enabled={true}>
                      <Text fontSize="sm" color="smPrimary">
                        {t("Your org")}
                      </Text>
                    </Explanation>
                  ),
                  ...customFields.map((field) => {
                    const fieldValue = getCustomFieldValue(contract, field.id);
                    if (!fieldValue) return null;
                    return (
                      <CustomContractFieldValue
                        key={field.id}
                        field={fieldValue}
                        onOpenRichTextPreview={(content) => setRichTextPreviewContent(content)}
                      />
                    );
                  }),
                  <Box key="actions">
                    <Button
                      leftIcon={<FaEye />}
                      variant="outline"
                      colorScheme="blue"
                      size="xs"
                      onClick={() => handleContractClick(contract)}
                    >
                      {t("View")}
                    </Button>
                  </Box>,
                ]) ?? []
              }
              isLoading={isLoading || isFetching}
              count={data?.count ?? 0}
              limit={filters.q.limit ?? 10}
              skip={filters.q.skip ?? 0}
              setSkip={(skip) => handleFilterChange({ q: { ...filters.q, skip } }, false)}
              onReset={handleReset}
              hasActiveFilters={hasActiveFilters}
            />
          </Box>
        </GridItem>
      </Grid>
    </Wrapper>
  );
};
